import { ChangeDetectionStrategy, Component, inject, Input } from "@angular/core";
import { Router } from "@angular/router";
import { ModalService } from "@core-services/modal.service";
import { MODAL_ANIMATION_TIMEOUT } from "@helpers/constants";
import { TRADE_TYPE } from "@models/trade-offer.model";
import { combineLatest, tap } from "rxjs";
import { getStakeableHelper } from "@helpers/functions";
import { StakeableAvailability } from "@modules/staking/domain/entities/stakable-availability.model";
import { AutoStakingFacade } from "@modules/staking/domain/+state/auto-staking.facade";
import { StakingFacade } from "@modules/staking/domain/+state/staking.facade";
import { Tradable } from "@models/interfaces/tradable";
import { MoSuccessVisionBannerState } from "@modules/web3/components/mo-success-vision-component/mo-success-vision-banner-state.model";
import { WEB3_FEE_BENEFFIT_FLOW, Web3VSNFeeBenefitFF } from "@modules/web3/interface/web3.benefit.ui.interface";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { map, take } from "rxjs/operators";
import { IVSNFeeTokenCollection } from "@models";
import { OrderFlow } from "@modules/limit-orders/interfaces/limit-order-rudderstack.model";
import { CheckoutEntryHelperService } from "@modules/checkout/services/checkout-entry-helper.service";
import { LimitOrdersTrackingFacade } from "@modules/limit-orders/services/orders-rudderstack.facade";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";

@Component({
	selector: "trade-swap-pending",
	templateUrl: "./trade-swap-pending.component.html",
	styleUrls: ["./trade-swap-pending.component.scss", "./../success-screen.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class TradeSwapPendingComponent {
	private readonly launchDarklyService = inject(LaunchDarklyService);

	swapType = TRADE_TYPE.SWAP;
	public showVisionWarning = false;
	public assetAvailableToStake = false;
	@Input() sourceAsset: Tradable;
	@Input() targetAsset: Tradable;
	@Input()
	public visionFeeTokenCollection: IVSNFeeTokenCollection;
	public visionFeeFlag$ = this.launchDarklyService
		.getFeatureConfig$<Web3VSNFeeBenefitFF>(LD_FLAG.WEB3_VSN_FEE_BENEFIT)
		.pipe(map((d) => d[WEB3_FEE_BENEFFIT_FLOW.swap]));
	public visionId$ = this.launchDarklyService
		.getFeatureConfig$<{
			pid: string;
		}>(LD_FLAG.VISION_PID)
		.pipe(map((data) => this.tradableListService.getItemByPid(data.pid)));
	protected readonly profileFacade = inject(NewProfileFacade);
	public isUkUser$ = this.profileFacade.isUkResident$();

	protected checkoutEntryHelper = inject(CheckoutEntryHelperService);
	protected limitOrdersTrackingFacade = inject(LimitOrdersTrackingFacade);

	private readonly autoStakingFacade = inject(AutoStakingFacade);
	private readonly stakingFacade = inject(StakingFacade);
	stakingVm$ = combineLatest({
		stakeables: this.stakingFacade.getStakeables$(),
		autoStakingState: this.autoStakingFacade.getOrFetchAutoStakingState$()
	}).pipe(
		tap((stakingVm) => {
			const stakeableHelper = getStakeableHelper(stakingVm.stakeables, this.targetAsset?.getId());
			this.assetAvailableToStake = stakeableHelper.stakeable?.availability === StakeableAvailability.ENABLED;
			this.showVisionWarning =
				stakeableHelper.isStakeable &&
				!stakeableHelper.stakeable?.autoStakingEnabled &&
				stakingVm.autoStakingState.autoStakingSwitchOn;
		})
	);

	constructor(
		private modalService: ModalService,
		private router: Router,
		private tradableListService: TradableListService
	) {}

	// close modal
	public close(role?: string, data: unknown = null): void {
		this.modalService.pop(data, role);
	}

	public getVisionBannerState(): MoSuccessVisionBannerState {
		if (
			this.visionFeeTokenCollection?.globalOptIn &&
			!this.visionFeeTokenCollection?.visionFeeDetail?.hasEnoughFunds
		) {
			return MoSuccessVisionBannerState.OPTEDININSUFFICIENTFUNDS;
		}
		return MoSuccessVisionBannerState.VISIONFEENOTACTIVE;
	}

	public handleBannerClick(): void {
		this.close();
		const bannerState = this.getVisionBannerState();
		if (bannerState === MoSuccessVisionBannerState.OPTEDININSUFFICIENTFUNDS) {
			this.limitOrdersTrackingFacade.updateFlow(OrderFlow.VisionUpSellBanner);
			this.visionId$.pipe(take(1)).subscribe((visionTradable) => {
				this.checkoutEntryHelper.onTradableSelect(visionTradable.getId());
			});
		} else if (bannerState === MoSuccessVisionBannerState.VISIONFEENOTACTIVE) {
			// TODO: review this
			this.router.navigateByUrl("/my-account/my-vision");
		} else {
			return;
		}
	}

	// navigate to portfolio
	public goToPortfolio(): void {
		this.close();
		setTimeout(() => {
			this.router.navigateByUrl("/portfolio");
		}, MODAL_ANIMATION_TIMEOUT);
	}

	protected readonly MoSuccessVisionBannerState = MoSuccessVisionBannerState;
}
